import { useState } from "react";
import { router, Link } from "@inertiajs/react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  Sparkles,
  Send,
  Loader2,
  Heart,
  Users,
  Briefcase,
  FileText,
} from "lucide-react";

interface Analysis {
  summary?: string;
  dating?: string;
  social?: string;
  professional?: string;
}

interface Photo {
  id: number;
  photoUrl: string;
  analyzed: boolean;
  analysis: Analysis | null;
  analyzedAt: string | null;
  submissionsCount: number;
  createdAt: string;
}

interface Props {
  photo: Photo;
  user: { email: string };
}

const ANALYSIS_SECTIONS = [
  {
    key: "summary" as const,
    label: "Resumo Geral",
    icon: FileText,
    color: "from-slate-500 to-slate-600",
  },
  { key: "dating" as const, label: "Encontros", icon: Heart, color: "from-rose-500 to-pink-600" },
  { key: "social" as const, label: "Social", icon: Users, color: "from-blue-500 to-indigo-600" },
  {
    key: "professional" as const,
    label: "Profissional",
    icon: Briefcase,
    color: "from-emerald-500 to-teal-600",
  },
];

export default function Show({ photo, user }: Props) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const handleAnalyze = () => {
    setIsAnalyzing(true);
    router.post(
      `/photos/${photo.id}/analyze`,
      {},
      {
        onFinish: () => setIsAnalyzing(false),
      }
    );
  };

  return (
    <Layout title="Detalhes da Foto" user={user}>
      <div className="mx-auto max-w-4xl px-4 py-10">
        <div className="mb-6">
          <Button
            asChild
            variant="ghost"
            className="gap-2 text-muted-foreground hover:text-foreground"
          >
            <Link href="/photos">
              <ArrowLeft size={16} />
              Voltar
            </Link>
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div>
            <Card className="border-border bg-card">
              <div className="flex items-center justify-center bg-background">
                <img src={photo.photoUrl} alt="Foto" className="w-full" />
              </div>
            </Card>

            <div className="mt-4 flex gap-3">
              <Button onClick={handleAnalyze} disabled={isAnalyzing} className="flex-1 gap-2">
                {isAnalyzing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Analisando...
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    {photo.analyzed ? "Reanalisar com IA" : "Analisar com IA"}
                  </>
                )}
              </Button>
              <Button asChild variant="outline" className="flex-1 gap-2">
                <Link href={`/submissions/new?photo_id=${photo.id}`}>
                  <Send size={16} />
                  Enviar para Votação
                </Link>
              </Button>
            </div>

            <p className="mt-3 text-center text-xs text-muted-foreground">
              {photo.submissionsCount === 0
                ? "Nenhuma votação criada"
                : `${photo.submissionsCount} votação${photo.submissionsCount > 1 ? "ões" : ""} criada${photo.submissionsCount > 1 ? "s" : ""}`}
            </p>
          </div>

          <div className="space-y-4">
            {photo.analyzed && photo.analysis ? (
              ANALYSIS_SECTIONS.map(({ key, label, icon: Icon, color }) => {
                const text = photo.analysis?.[key];
                if (!text) return null;

                return (
                  <Card key={key} className="border-border bg-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <div
                          className={`flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br ${color}`}
                        >
                          <Icon size={14} className="text-white" />
                        </div>
                        {label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-relaxed text-foreground/80">{text}</p>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Card className="border-border bg-card">
                <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                  <Sparkles size={32} className="text-muted-foreground" />
                  <div>
                    <p className="font-medium">Análise de IA</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Clique em "Analisar com IA" para receber feedback personalizado sobre sua
                      foto.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
